import { Node } from "@xyflow/react";
import { atom } from "jotai";
import { loadable } from "jotai/utils";

import { DefaultNodeData, NodeData, Service, AgentFetchResult } from "../types";
import { zapMetadataAtomFamily_SUSPENDS } from "../zaps";
import { agentAtomFamily_SUSPENDS } from "../apps";
import {
  flattenOneLevel,
  getUniqueAppsFromSteps,
  isZapErrorObject,
} from "../utils";
import { getUniqueAgentWorkflowApps } from "../../../canvas/src/utils/getUniqueAgentWorkflowApps";

import { nodesAtom } from "./nodesAtom";
import { zapNodesAtom } from "./zapNodesAtom";
import { uniqueAgentIdsAtom } from "./uniqueAgentIdsAtom";

export const uniqueZapIdsAtom = atom((get) => {
  const zapNodes = get(zapNodesAtom);

  const uniqueZapIds = new Set(
    zapNodes
      .filter((node) => Boolean(node.data.zapId)) // zapId is null when a Zap node is first created but no Zap has been selected yet
      .map((node) => node.data.zapId!),
  );
  return Array.from(uniqueZapIds);
});

uniqueZapIdsAtom.debugLabel = "uniqueZapIdsAtom";

/**
 * Returns an array of unique app IDs from all nodes. Handles default, Zap, and Agent nodes.
 * For Zap nodes, it recursively retrieves all app IDs from path steps.
 * For Agent nodes, it retrieves unique workflow apps from the agent's workflow.
 */
export const uniqueAppsForAllNodesAtom_SUSPENDS = atom<Promise<string[]>>(
  async (get) => {
    const nodes: Node<NodeData>[] = get(nodesAtom);
    const defaultNodes = nodes.filter((node) => node.type === "default");
    const uniqueZapIds = get(uniqueZapIdsAtom);
    const uniqueAgentIds = get(uniqueAgentIdsAtom);

    const zapNodeApps = await Promise.all(
      uniqueZapIds.map(async (zapId: string) => {
        const zap = await get(zapMetadataAtomFamily_SUSPENDS(zapId));
        if (!zap || isZapErrorObject(zap)) {
          return [];
        }

        const steps = zap.steps || [];
        return getUniqueAppsFromSteps(steps);
      }),
    );

    const zapNodeAppsFlattened = flattenOneLevel(zapNodeApps);

    const agentNodeApps = await Promise.all(
      uniqueAgentIds.map(async (agentId: string) => {
        const agentResult = (await get(agentAtomFamily_SUSPENDS(agentId))) as
          | AgentFetchResult
          | null
          | undefined;

        // Handle null (500s) or non-success states (404 / 403)
        if (!agentResult || agentResult.type !== "success") {
          return [];
        }

        return getUniqueAgentWorkflowApps(agentResult.data.workflow);
      }),
    );

    const agentNodeAppsFlattened = flattenOneLevel(agentNodeApps);

    const defaultNodeApps = defaultNodes
      .reduce(
        (acc, curr) => [
          ...acc,
          ...((curr.data as DefaultNodeData).services || []),
        ],
        [] as Service[],
      )
      .filter(Boolean);

    const allApps: string[] = [
      ...defaultNodeApps.map((app) => app.currentImplementation?.id ?? ""),
      ...zapNodeAppsFlattened,
      ...agentNodeAppsFlattened,
    ];

    // Split on "@" to get base app ID without version numbers to avoid duplicates
    // e.g., "SlackCLIAPI@1.7.0" becomes "SlackCLIAPI"
    const uniqueBaseAppIdsSet = new Set(
      allApps.map((appId) => appId.split("@")[0]),
    );

    return Array.from(uniqueBaseAppIdsSet);
  },
);

uniqueAppsForAllNodesAtom_SUSPENDS.debugLabel =
  "uniqueAppsForAllNodesAtom_SUSPENDS";

// Does NOT suspend due to loadable usage
export const loadableAllNodesUniqueAppsAtom = loadable(
  uniqueAppsForAllNodesAtom_SUSPENDS,
);
loadableAllNodesUniqueAppsAtom.debugLabel = "loadableAllNodesUniqueAppsAtom";
